import { useSyncExternalStore } from "react";

/**
 * How long a just-synced record shows its "Synced" confirmation badge.
 *
 * The CSS `synced-confirm` keyframe in `index.css` runs for exactly this
 * duration, so the badge's opacity is already 0 by the time the entry
 * expires and the badge unmounts — the disappearance is seamless. See
 * [[offline-record-outbox]] § Success and [[photo-grid-item.spec]].
 */
export const SYNCED_CONFIRM_DURATION_MS = 1500;

// Server-record ids currently inside their post-sync confirmation window.
const justSyncedIds = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getServerSnapshot(): boolean {
  return false;
}

/**
 * Mark a record as just-synced. Its grid tile shows the "Synced"
 * confirmation badge for {@link SYNCED_CONFIRM_DURATION_MS}, after which the
 * id is dropped and subscribed tiles re-render badge-less.
 *
 * Called from the outbox drain's `onSuccess` the moment a record's create
 * lands. The timer lives at module scope, so a tile recycled by grid
 * virtualization mid-window neither loses nor restarts the confirmation.
 */
export function markRecordJustSynced(recordId: string): void {
  justSyncedIds.add(recordId);
  emit();
  setTimeout(() => {
    justSyncedIds.delete(recordId);
    emit();
  }, SYNCED_CONFIRM_DURATION_MS);
}

/**
 * Whether `recordId` is inside its post-sync confirmation window — drives
 * the transient "Synced" badge on [[photo-grid-item.spec]]. In-memory and
 * session-scoped: a record synced in a previous session is never flagged.
 */
export function useJustSynced(recordId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => justSyncedIds.has(recordId),
    getServerSnapshot,
  );
}
